import { z } from 'zod'
import type { OpenTab } from '@/components/features/document-tabs'

/**
 * Storage Schemas
 *
 * Runtime validation schemas for localStorage values using Zod.
 * These ensure type safety and catch corrupted/invalid data.
 */

/** Keep in sync with OpenTab['type'] in document-tabs.tsx */
const openTabTypeSchema = z.enum([
  'work-spec',
  'note',
  'voice-memo',
  'flow',
  'todo',
  'meeting',
  'idea',
  'research',
  'skill',
  'quiz',
  'explorer',
  'home',
  'inbox',
  'inbox-thread',
  'chat-conversation',
  'graph-view',
  'workspace-home',
  'integration-basecamp',
  'integration-basecamp-todo',
  'integration-basecamp-todo-list',
  'integration-basecamp-triage',
  'integration-voice-memos',
  'integration-product-board',
  'integration-knowledge-hub',
  'integration-google-drive',
  'integration-google-drive-preview',
  'integration-nextcloud',
  'platform-skill',
  'search-view',
  'person-view',
  'edit-history',
])

const openTabSchema = z.object({
  id: z.string(),
  title: z.string(),
  type: openTabTypeSchema,
  isDirty: z.boolean().optional(),
  isPinned: z.boolean().optional(),
  tabSlotId: z.string().optional(),
  workspaceId: z.string().nullable().optional(),
  filePath: z.string().nullable().optional(),
  initialQuery: z.string().optional(),
  searchNamedView: z.enum(['search', 'activity']).optional(),
  searchPath: z.string().optional(),
  searchUpdatedBy: z.string().optional(),
  personUserId: z.string().optional(),
  explorerData: z.object({
    folderPath: z.string().nullable(),
  }).optional(),
  inboxThreadId: z.string().optional(),
  chatThreadId: z.string().optional(),
  integrationData: z.object({
    integrationId: z.string(),
    projectId: z.string().optional(),
    todoId: z.string().optional(),
    listId: z.string().optional(),
    listName: z.string().optional(),
    projectName: z.string().optional(),
    section: z.string().optional(),
    organizationId: z.string().optional(),
    workspaceSlug: z.string().optional(),
    todoData: z.unknown().optional(),
    highlightStepId: z.string().optional(),
    statusFilter: z.enum(['review-requested', 'needs-review']).nullable().optional(),
    breadcrumbTrail: z.array(z.object({
      id: z.string(),
      title: z.string(),
      type: z.enum(['projects', 'project', 'todoset', 'todolist', 'section']),
    })).optional(),
    driveFileId: z.string().optional(),
    driveMimeType: z.string().optional(),
    driveWebViewLink: z.string().optional(),
    importedDocumentId: z.string().optional(),
    driveIsShortcut: z.boolean().optional(),
    driveShortcutSourceId: z.string().optional(),
    nextcloudPath: z.string().optional(),
    nextcloudKind: z.enum(['file', 'folder']).optional(),
    nextcloudFileId: z.string().optional(),
    nextcloudPreviewPath: z.string().optional(),
    nextcloudSurface: z.enum(['browse', 'document']).optional(),
  }).optional(),
})

/**
 * Soft-migrate open tabs: drop only invalid entries instead of wiping the whole array.
 * Empty / fully-invalid → single Home tab (same as default hydrate).
 */
function sanitizeOpenTabs(value: unknown): OpenTab[] {
  if (!Array.isArray(value)) {
    return [{ id: 'home', title: 'Home', type: 'home', isDirty: false, tabSlotId: 'tab-main' }]
  }
  const kept: OpenTab[] = []
  for (const item of value) {
    const parsed = openTabSchema.safeParse(item)
    if (parsed.success) {
      kept.push(parsed.data as OpenTab)
    }
  }
  if (kept.length === 0) {
    return [{ id: 'home', title: 'Home', type: 'home', isDirty: false, tabSlotId: 'tab-main' }]
  }
  return kept
}

/** Renormalize panel percents when float drift or stale layouts fail the ~100 sum check. */
function normalizePanelTriple(value: unknown): number[] {
  if (!Array.isArray(value) || value.length !== 3 || !value.every((n) => typeof n === 'number' && Number.isFinite(n))) {
    return [20, 48, 32]
  }
  const sum = value[0] + value[1] + value[2]
  if (sum >= 99.9 && sum <= 100.1) {
    return value as number[]
  }
  if (sum <= 0) {
    return [20, 48, 32]
  }
  return value.map((n) => (n / sum) * 100)
}

export const StorageSchemas = {
  // UI Layout
  panelSizes: z.preprocess(
    normalizePanelTriple,
    z.array(z.number()).length(3).refine(
      (arr) => {
        const sum = arr.reduce((a, b) => a + b, 0)
        return sum >= 99.9 && sum <= 100.1
      },
      { message: 'Panel sizes must sum to ~100' }
    ),
  ),

  boolean: z.boolean(),

  number: z.number(),

  /** Optional px ceiling; null = auto (Open Tabs semi-auto height). */
  numberNullable: z.number().nullable(),

  sidebarTab: z.union([
    z.enum([
      'browse-nav',
      'browse',
      'documents-tree',
      'documents',
      'inbox',
      'flows',
      'chats',
      'drive',
      'basecamp',
      'nextcloud',
      'graph',
      'tags',
      'trash',
    ]),
    z.custom<`integration-${string}`>(
      (val): val is `integration-${string}` => typeof val === 'string' && val.startsWith('integration-'),
    ),
  ]) as z.ZodType<
    | 'browse-nav'
    | 'browse'
    | 'documents-tree'
    | 'documents'
    | 'inbox'
    | 'flows'
    | 'chats'
    | 'drive'
    | 'basecamp'
    | 'nextcloud'
    | 'graph'
    | 'tags'
    | 'trash'
    | `integration-${string}`
  >,

  editorMode: z.enum(['wysiwyg', 'source']),

  /** Default = centred ~760px column; full = prose uses panel width */
  pageWidthMode: z.enum(['default', 'full']),

  /** kind string -> whether Source mode includes frontmatter (user override per kind) */
  sourceFrontmatterPrefs: z.record(z.string(), z.boolean()),

  diffViewMode: z.enum(['gutter', 'unified']),

  viewMode: z.enum(['card', 'grid', 'list', 'tree']),

  provider: z.enum(['openai', 'anthropic', 'groq', 'xai', 'localai', 'ollama']),

  enabledProviders: z.array(z.enum(['openai', 'anthropic', 'groq', 'xai', 'localai', 'ollama'])),

  theme: z.enum(['light', 'dark', 'system']),

  /** Secondary dock enter: nudge | fade | crisp | instant (gallery #panel-motion) */
  shellPanelMotionStyle: z.enum(['nudge', 'fade', 'crisp', 'instant']),

  openTabs: z.preprocess(sanitizeOpenTabs, z.array(openTabSchema)) as z.ZodType<OpenTab[]>,

  stringArray: z.array(z.string()),

  /** workspaceId -> tab id last focused in that workspace */
  lastActiveTabByWorkspace: z.record(z.string(), z.string()),

  /** treeKey -> segment path from that tree root */
  documentsFocusByTreeKey: z.record(z.string(), z.array(z.string())),

  numberArray: z.array(z.number()),

  graphGroupBy: z.enum(['none', 'folder', 'project', 'client']),

  // Basecamp-specific
  basecampWidgetSizes: z.array(z.number()).length(3).refine(
    (arr) => {
      const sum = arr.reduce((a, b) => a + b, 0)
      return sum >= 99.9 && sum <= 100.1
    },
    { message: 'Widget sizes must sum to ~100' }
  ),

  // Document panel sizes (2 panels)
  documentPanelSizes: z.array(z.number()).length(2).refine(
    (arr) => {
      const sum = arr.reduce((a, b) => a + b, 0)
      return sum >= 99.9 && sum <= 100.1
    },
    { message: 'Document panel sizes must sum to ~100' }
  ),

  browseFolderPrefs: z.object({
    alwaysShowPaths: z.array(z.string()),
    hiddenManualPaths: z.array(z.string()),
  }),

  smtpTestRecipients: z.object({
    lastTo: z.string(),
    recent: z.array(z.string()).max(12),
  }),

  basecampTodosView: z.object({
    listMode: z.enum(['me', 'org']),
    scope: z.enum(['all', 'hop_only', 'client_only']),
    statusFilter: z.enum(['all', 'overdue', 'due_today', 'overdue_and_today']),
    assigneeFacet: z.enum(['all', 'me', 'hop', 'clients', 'unassigned', 'person']),
    assigneePerson: z.string().nullable(),
    q: z.string().default(''),
  }),

  basecampActivityView: z.object({
    who: z.enum(['org', 'team', 'mine']),
    kind: z.enum(['all', 'comments', 'completions', 'updates']),
    linkedOnly: z.boolean(),
    projectId: z.string(),
    q: z.string().default(''),
  }),
} as const

